import { useState, useEffect, useCallback } from 'react';
import { academyStudentService } from '../services/academyStudentService';
import type { ScheduleResponse } from '../services/schedule';
import { expandScheduleOccurrences } from '../utils/expandScheduleOccurrences';
import type { ScheduleOccurrence } from '../utils/scheduleOccurrenceTypes';

export function useStudentProfileSchedule(
  academyId: string | undefined,
  studentId: string | undefined,
  enabled: boolean
) {
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [occurrences, setOccurrences] = useState<ScheduleOccurrence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    if (!academyId || !studentId) return;
    try {
      setLoading(true);
      setError(null);
      const schedule = await academyStudentService.getStudentSchedule(academyId, studentId);
      setData(schedule);
      setOccurrences(expandScheduleOccurrences(schedule));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
      setData(null);
      setOccurrences([]);
    } finally {
      setLoading(false);
    }
  }, [academyId, studentId]);

  useEffect(() => {
    if (enabled && academyId && studentId) {
      void fetchSchedule();
    }
  }, [enabled, academyId, studentId, fetchSchedule]);

  return {
    data,
    occurrences,
    loading,
    error,
    refetch: fetchSchedule,
  };
}
